import {
  Pagination as PaginationRoot,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  getPageUrl: (page: number) => string;
  prevButtonDataTestId: string;
  nextButtonDataTestId: string;
}

export default function Pagination({
  currentPage,
  totalPages,
  getPageUrl,
  prevButtonDataTestId,
  nextButtonDataTestId,
}: PaginationProps) {
  // Clamp current page to valid range
  const clampedPage = Math.max(1, Math.min(currentPage, totalPages));

  const isFirstPage = clampedPage === 1;
  const isLastPage = clampedPage === totalPages;

  // Determine if we need ellipses
  const showLeftEllipsis = clampedPage > 2;
  const showRightEllipsis = clampedPage < totalPages - 1;

  return (
    <div>
      {/* Mobile: Previous | Page X of Y | Next */}
      <PaginationRoot className="sm:hidden">
        <PaginationContent>
          <PaginationItem>
            {isFirstPage ? (
              <span
                data-testid={prevButtonDataTestId}
                className="inline-flex items-center justify-center gap-1 px-2.5 h-9 text-sm opacity-50 cursor-not-allowed"
              >
                Previous
              </span>
            ) : (
              <PaginationPrevious
                href={getPageUrl(clampedPage - 1)}
                data-testid={prevButtonDataTestId}
              />
            )}
          </PaginationItem>

          <span className="text-sm text-muted-foreground px-2">
            Page {clampedPage} of {totalPages}
          </span>

          <PaginationItem>
            {isLastPage ? (
              <span
                data-testid={nextButtonDataTestId}
                className="inline-flex items-center justify-center gap-1 px-2.5 h-9 text-sm opacity-50 cursor-not-allowed"
              >
                Next
              </span>
            ) : (
              <PaginationNext
                href={getPageUrl(clampedPage + 1)}
                data-testid={nextButtonDataTestId}
              />
            )}
          </PaginationItem>
        </PaginationContent>
      </PaginationRoot>

      {/* Desktop: Previous | 1 | ... | Current | ... | Last Page | Next */}
      <PaginationRoot className="hidden sm:flex">
        <PaginationContent>
          {/* Previous */}
          <PaginationItem>
            {isFirstPage ? (
              <span
                data-testid={prevButtonDataTestId}
                className="inline-flex items-center justify-center gap-1 px-2.5 h-9 text-sm opacity-50 cursor-not-allowed"
              >
                Previous
              </span>
            ) : (
              <PaginationPrevious
                href={getPageUrl(clampedPage - 1)}
                data-testid={prevButtonDataTestId}
              />
            )}
          </PaginationItem>

          {/* First page */}
          {!isFirstPage && (
            <PaginationItem>
              <PaginationLink href={getPageUrl(1)}>1</PaginationLink>
            </PaginationItem>
          )}

          {/* Left ellipsis */}
          {showLeftEllipsis && (
            <PaginationItem>
              <PaginationEllipsis />
            </PaginationItem>
          )}

          {/* Current page */}
          <PaginationItem>
            <PaginationLink href={getPageUrl(clampedPage)} isActive>
              {clampedPage}
            </PaginationLink>
          </PaginationItem>

          {/* Right ellipsis */}
          {showRightEllipsis && (
            <PaginationItem>
              <PaginationEllipsis />
            </PaginationItem>
          )}

          {/* Last page */}
          {!isLastPage && (
            <PaginationItem>
              <PaginationLink href={getPageUrl(totalPages)}>
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          )}

          {/* Next */}
          <PaginationItem>
            {isLastPage ? (
              <span
                data-testid={nextButtonDataTestId}
                className="inline-flex items-center justify-center gap-1 px-2.5 h-9 text-sm opacity-50 cursor-not-allowed"
              >
                Next
              </span>
            ) : (
              <PaginationNext
                href={getPageUrl(clampedPage + 1)}
                data-testid={nextButtonDataTestId}
              />
            )}
          </PaginationItem>
        </PaginationContent>
      </PaginationRoot>
    </div>
  );
}
